import React, { FC, useEffect, useMemo, useState } from "react";
import { Table, Tooltip } from "antd";
import moment from "moment";
import { BeamEvent } from "./types";
import BeamIcon from "./BeamIcon";
import "./MessageTypes.scss";

/**
 * Calendar-based Event message type.
 *
 * The client's own wording drives the three modes (it overrides the generated
 * spec on this one):
 *   Daily   - simple table with the details, no thumbnail
 *   Weekly  - the 7 days of that week with the events listed under each
 *   Monthly - a calendar
 * Everything except Daily carries a small thumbnail.
 */

export type EventViewMode = "day" | "week" | "month";

interface EventsCardProps {
  events: BeamEvent[];
  /** Anchor date the three views are calculated from. Defaults to today. */
  initialDate?: moment.Moment;
  onSelectEvent?: (event: BeamEvent) => void;
}

const MODES: { key: EventViewMode; label: string }[] = [
  { key: "day", label: "Daily" },
  { key: "week", label: "Weekly" },
  { key: "month", label: "Monthly" },
];

const EventDot: FC = () => <span className="mt-events__dot" />;

/** Small square thumbnail; falls back to the event's initial. */
const EventThumb: FC<{ event: BeamEvent; size?: number }> = ({
  event,
  size = 28,
}) => {
  const [failed, setFailed] = useState(false);

  if (!event.thumbnail || failed) {
    return (
      <span
        className="mt-events__thumb mt-events__thumb--fallback"
        style={{ width: size, height: size }}
      >
        {event.title.charAt(0).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      className="mt-events__thumb"
      style={{ width: size, height: size }}
      src={event.thumbnail}
      alt=""
      onError={() => setFailed(true)}
    />
  );
};

const EventsCard: FC<EventsCardProps> = ({
  events,
  initialDate,
  onSelectEvent,
}) => {
  const [mode, setMode] = useState<EventViewMode>("day");
  // Anchor moves by day / week / month depending on the active view.
  const [anchor, setAnchor] = useState<moment.Moment>(
    initialDate ? initialDate.clone() : moment(events[0]?.start || undefined)
  );
  // A state initialiser runs once, and the stream is still empty on that
  // first render — so the anchor above lands on today and stays there even
  // after the events arrive, showing "no events" to a page that has some.
  // Once the first event is known, move to it, but only while the reader has
  // not navigated: yanking the date out from under them would be worse.
  const [userMoved, setUserMoved] = useState(false);
  const firstStart = events[0]?.start;
  useEffect(() => {
    if (initialDate || userMoved || !firstStart) return;
    setAnchor(moment(firstStart));
  }, [firstStart, initialDate, userMoved]);

  const step = (direction: 1 | -1) => {
    const unit = mode === "day" ? "day" : mode === "week" ? "week" : "month";
    setUserMoved(true);
    setAnchor(anchor.clone().add(direction, unit));
  };

  const rangeLabel = useMemo(() => {
    if (mode === "day") return anchor.format("dddd, D MMM YYYY");
    if (mode === "week") {
      const start = anchor.clone().startOf("week");
      const end = anchor.clone().endOf("week");
      return `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`;
    }
    return anchor.format("MMMM YYYY");
  }, [mode, anchor]);

  /** Events grouped by YYYY-MM-DD so week/month cells are a cheap lookup. */
  const byDay = useMemo(() => {
    return events.reduce<Record<string, BeamEvent[]>>((acc, event) => {
      const key = moment(event.start).format("YYYY-MM-DD");
      (acc[key] = acc[key] || []).push(event);
      return acc;
    }, {});
  }, [events]);

  const eventsOn = (date: moment.Moment) =>
    (byDay[date.format("YYYY-MM-DD")] || []).sort((a, b) =>
      a.start.localeCompare(b.start)
    );

  /* ---------------- Daily: details table, no thumbnail ---------------- */

  const renderDay = () => {
    const rows = eventsOn(anchor);

    const columns = [
      {
        title: "Time",
        dataIndex: "start",
        width: 110,
        render: (_: string, row: BeamEvent) => (
          <span className="mt-events__time">
            {moment(row.start).format("h:mm A")}
            <em>{moment(row.end).format("h:mm A")}</em>
          </span>
        ),
      },
      {
        title: "Event",
        dataIndex: "title",
        render: (title: string, row: BeamEvent) => (
          <div className="mt-events__cell-title">
            <EventDot />
            <span>{title}</span>
          </div>
        ),
      },
      {
        title: "Location",
        dataIndex: "location",
        render: (location: string) => (
          <span className="mt-events__muted">
            <BeamIcon name="location" size={13} /> {location}
          </span>
        ),
      },
      {
        title: "Personnel",
        dataIndex: "personnel",
        width: 150,
        render: (personnel: string[]) => (
          <Tooltip title={personnel.join(", ")}>
            <span className="mt-events__muted">
              <BeamIcon name="community" size={13} /> {personnel.length} assigned
            </span>
          </Tooltip>
        ),
      },
      {
        title: "RSVP",
        dataIndex: "rsvp",
        width: 130,
        render: (rsvp: BeamEvent["rsvp"]) => (
          <span className="mt-events__rsvp">
            <b className="is-yes">{rsvp.yes}</b>
            <b className="is-maybe">{rsvp.maybe}</b>
            <b className="is-no">{rsvp.no}</b>
          </span>
        ),
      },
    ];

    return (
      <Table
        className="mt-events__table"
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={rows}
        columns={columns as any}
        locale={{ emptyText: "No events scheduled for this day." }}
        onRow={(row) => ({ onClick: () => onSelectEvent?.(row) })}
      />
    );
  };

  /* ---------------- Weekly: 7 days of that week ---------------- */

  const renderWeek = () => {
    const start = anchor.clone().startOf("week");
    const days = Array.from({ length: 7 }, (_, i) => start.clone().add(i, "day"));

    return (
      <div className="mt-events__week">
        {days.map((date) => {
          const dayEvents = eventsOn(date);
          const isToday = date.isSame(moment(), "day");

          return (
            <div
              key={date.format("YYYY-MM-DD")}
              className={`mt-events__week-col ${isToday ? "is-today" : ""}`}
            >
              <div className="mt-events__week-head">
                <span className="mt-events__week-dow">
                  {date.format("ddd")}
                </span>
                <span className="mt-events__week-date">
                  {date.format("D")}
                </span>
              </div>

              <div className="mt-events__week-body">
                {dayEvents.length === 0 && (
                  <span className="mt-events__week-empty">—</span>
                )}
                {dayEvents.map((event) => (
                  <button
                    key={event.id}
                    type="button"
                    className="mt-events__chip"
                    onClick={() => onSelectEvent?.(event)}
                  >
                    <EventThumb event={event} size={24} />
                    <span className="mt-events__chip-text">
                      <b>{moment(event.start).format("h:mm A")}</b>
                      <em>{event.title}</em>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  /* ---------------- Monthly: calendar ---------------- */

  const renderMonth = () => {
    const monthStart = anchor.clone().startOf("month");
    const gridStart = monthStart.clone().startOf("week");
    // 6 rows covers every possible month/first-weekday combination.
    const cells = Array.from({ length: 42 }, (_, i) =>
      gridStart.clone().add(i, "day")
    );

    return (
      <div className="mt-events__month">
        <div className="mt-events__month-dows">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((dow) => (
            <span key={dow}>{dow}</span>
          ))}
        </div>

        <div className="mt-events__month-grid">
          {cells.map((date) => {
            const dayEvents = eventsOn(date);
            const outside = !date.isSame(anchor, "month");

            return (
              <div
                key={date.format("YYYY-MM-DD")}
                className={`mt-events__month-cell ${
                  outside ? "is-outside" : ""
                } ${date.isSame(moment(), "day") ? "is-today" : ""}`}
              >
                <span className="mt-events__month-date">
                  {date.format("D")}
                </span>

                <div className="mt-events__month-events">
                  {dayEvents.slice(0, 2).map((event) => (
                    <Tooltip
                      key={event.id}
                      title={`${moment(event.start).format("h:mm A")} · ${
                        event.title
                      }`}
                    >
                      <button
                        type="button"
                        className="mt-events__month-event"
                        onClick={() => onSelectEvent?.(event)}
                      >
                        <EventThumb event={event} size={20} />
                        <span>{event.title}</span>
                      </button>
                    </Tooltip>
                  ))}
                  {dayEvents.length > 2 && (
                    <span className="mt-events__month-more">
                      +{dayEvents.length - 2} more
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <section className="mt-card mt-events">
      <header className="mt-card__head">
        <div className="mt-events__nav">
          <button type="button" onClick={() => step(-1)} aria-label="Previous">
            <BeamIcon name="next" size={13} className="transform-flip" />
          </button>
          <h4>{rangeLabel}</h4>
          <button type="button" onClick={() => step(1)} aria-label="Next">
            <BeamIcon name="next" size={13} />
          </button>
        </div>

        <div className="mt-segmented">
          {MODES.map((item) => (
            <button
              key={item.key}
              type="button"
              className={mode === item.key ? "is-active" : ""}
              onClick={() => setMode(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </header>

      <div className="mt-card__body">
        {mode === "day" && renderDay()}
        {mode === "week" && renderWeek()}
        {mode === "month" && renderMonth()}
      </div>
    </section>
  );
};

export default EventsCard;
